import React, { useState, useEffect } from 'react';
import './Products.css';
import { getAllProducts } from '../../services/firestoreService';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPriceHistory, setShowPriceHistory] = useState(false);

  // Load products from Firebase only
  useEffect(() => {
    // Clear old localStorage cache once per session
    const hasCleared = sessionStorage.getItem('productsCleared');
    if (!hasCleared) {
      localStorage.removeItem('adminProducts');
      sessionStorage.setItem('productsCleared', 'true');
      console.log('✅ Cleared old products cache');
    }

    const loadProducts = async () => {
      try {
        console.log('Loading products from Firebase...');
        const productsData = await getAllProducts();
        setProducts(productsData);
        console.log(`Loaded ${productsData.length} products from Firebase`);
      } catch (error) {
        console.error('Error loading products from Firebase:', error);
      }
    };

    // Load initially
    loadProducts();

    // Refresh products every 10 seconds to get latest data
    const interval = setInterval(loadProducts, 10000);

    return () => {
      clearInterval(interval);
    };
  }, []);



  return (
    <div className="products-page">
      {/* Hero Section */}
      <section className="products-hero">
        <div className="container">
          <h1>ಕೃಷಿ ಉತ್ಪನ್ನಗಳು</h1>
          <p>ಮಾರುಕಟ್ಟೆ ದರಗಳೊಂದಿಗೆ ಪ್ರೀಮಿಯಂ ಗುಣಮಟ್ಟದ ಕೃಷಿ ಉತ್ಪನ್ನಗಳು</p>
        </div>
      </section>

      {/* Simple Market Rates Section */}
      <section className="simple-products-section">
        <div className="container">
          <div className="simple-header">
            <h2>ಮಾರುಕಟ್ಟೆ ದರಗಳು</h2>
            <p>ಇಂದಿನ ಬೆಲೆಗಳು</p>
          </div>
          
          {products.length === 0 ? (
            <div className="simple-empty">
              <i className="fas fa-box-open"></i>
              <p>ಪ್ರಸ್ತುತ ಯಾವುದೇ ಉತ್ಪನ್ನಗಳು ಲಭ್ಯವಿಲ್ಲ</p>
              <a href="tel:9902671238" className="simple-call-btn">
                <i className="fas fa-phone"></i> 9902671238
              </a>
            </div>
          ) : (
            <div className="simple-products-grid">
              {products.map((product) => (
                <div key={product.id} className="simple-product-card">
                  {/* Clickable area for price history */}
                  <div 
                    className="simple-card-clickable"
                    onClick={() => {
                      if (product.priceHistory && product.priceHistory.length > 0) {
                        setSelectedProduct(product);
                        setShowPriceHistory(true);
                      }
                    }}
                    style={{ cursor: product.priceHistory && product.priceHistory.length > 0 ? 'pointer' : 'default' }}
                  >
                    {/* Product Info */}
                    <div className="simple-product-info">
                      {/* Price Change Badge */}
                      {(() => {
                        if (product.priceHistory && product.priceHistory.length >= 2) {
                          // Compare current price with second-to-last price (not the last one which might be the same)
                          const currentPrice = product.currentPrice;
                          const previousPrice = product.priceHistory[product.priceHistory.length - 2].price;
                          const priceDiff = currentPrice - previousPrice;
                          
                          if (priceDiff > 0) {
                            return (
                              <span className="price-change-badge up">
                                <i className="fas fa-arrow-up"></i> ₹{Math.abs(priceDiff).toLocaleString('en-IN')} ಹೆಚ್ಚು
                              </span>
                            );
                          } else if (priceDiff < 0) {
                            return (
                              <span className="price-change-badge down">
                                <i className="fas fa-arrow-down"></i> ₹{Math.abs(priceDiff).toLocaleString('en-IN')} ಕಮ್ಮಿ
                              </span>
                            );
                          } else {
                            return (
                              <span className="price-change-badge same">
                                <i className="fas fa-minus"></i> ಬದಲಾವಣೆ ಇಲ್ಲ
                              </span>
                            );
                          }
                        } else if (product.priceHistory && product.priceHistory.length === 1) {
                          // Only one price in history, compare with that
                          const currentPrice = product.currentPrice;
                          const previousPrice = product.priceHistory[0].price;
                          const priceDiff = currentPrice - previousPrice;
                          
                          if (priceDiff > 0) {
                            return (
                              <span className="price-change-badge up">
                                <i className="fas fa-arrow-up"></i> ₹{Math.abs(priceDiff).toLocaleString('en-IN')} ಹೆಚ್ಚು
                              </span>
                            );
                          } else if (priceDiff < 0) {
                            return (
                              <span className="price-change-badge down">
                                <i className="fas fa-arrow-down"></i> ₹{Math.abs(priceDiff).toLocaleString('en-IN')} ಕಮ್ಮಿ
                              </span>
                            );
                          } else {
                            return (
                              <span className="price-change-badge same">
                                <i className="fas fa-minus"></i> ಬದಲಾವಣೆ ಇಲ್ಲ
                              </span>
                            );
                          }
                        } else {
                          return <span className="simple-badge">ಹೊಸ ಬೆಲೆ</span>;
                        }
                      })()}
                      {product.priceHistory && product.priceHistory.length > 0 && (
                        <span className="history-indicator">
                          <i className="fas fa-history"></i> ಹಿಂದಿನ ದರಗಳು
                        </span>
                      )}
                    
                      {/* Product Name */}
                      <div className="simple-name">
                        <h3>{product.name}</h3>
                        <p>{product.englishName}</p>
                      </div>
                      
                      {/* Price Box */}
                      <div className="simple-price-box">
                        <div className="simple-price">
                          ₹ {product.currentPrice.toLocaleString('en-IN')}
                        </div>
                        <div className="simple-unit">ಪ್ರತಿ ಕ್ವಿಂಟಾಲ್</div>
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="simple-actions">
                    <a href="tel:9902671238" className="simple-btn call" onClick={(e) => e.stopPropagation()}>
                      <i className="fas fa-phone"></i> ಈಗ ಕರೆ ಮಾಡಿ
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Selling Process Section */}
      <section className="selling-process-section">
        <div className="container">
          <h2>ಬೇಳೆ ಮಾರುವ ವಿಧಾನ</h2>
          <div className="process-steps-grid">
            <div className="process-step-card">
              <div className="step-icon">
                <i className="fas fa-phone-alt"></i>
              </div>
              <h3>ಭೇಟಿ ಸೇವೆ 9902671238</h3>
              <p>ನೀವು ನಮ್ಮ ಸಂಖ್ಯೆಗೆ ಕರೆ ಮಾಡಿದರೆ, ನಾವು ನಿಮ್ಮ ಹೊಲಕ್ಕೆ ಭೇಟಿ ನೀಡುತ್ತೇವೆ.</p>
            </div>
            <div className="process-step-card">
              <div className="step-icon">
                <i className="fas fa-microscope"></i>
              </div>
              <h3>ಗುಣಮಟ್ಟ ಪರಿಶೀಲನೆ</h3>
              <p>ನಾವು ಗುಣಮಟ್ಟ ಪರಿಶೀಲನೆ ಮಾಡುತ್ತೇವೆ ಮತ್ತು ಮೊದಲಿಗೆ ಸ್ವಲ್ಪ ಮೊತ್ತವನ್ನು ಮುಂಗಡವಾಗಿ ಪಾವತಿಸುತ್ತೇವೆ.</p>
            </div>
            <div className="process-step-card">
              <div className="step-icon">
                <i className="fas fa-truck"></i>
              </div>
              <h3>ಸಾಗಣೆ ಸೇವೆ</h3>
              <p>ನಮ್ಮ ಲಾರಿ ಅಥವಾ ವಾಹನ ನಿಮ್ಮ ಹೊಲಕ್ಕೆ ಬಂದು ಸರಿಯಾದ ತೂಕ ತೂಗಿ ಉತ್ಪನ್ನವನ್ನು ಕೊಂಡೊಯ್ಯುತ್ತದೆ.</p>
            </div>
            <div className="process-step-card">
              <div className="step-icon">
                <i className="fas fa-money-bill-wave"></i>
              </div>
              <h3>ಹಣ</h3>
              <p>ಎರಡು ದಿನಗಳ ಒಳಗೆ ನಿಮ್ಮ ಖಾತೆಗೆ ಸಂಪೂರ್ಣ ಹಣ ಜಮೆ ಮಾಡಲಾಗುತ್ತದೆ.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/919902671238"
        className="whatsapp-button"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contact on WhatsApp"
      >
        <i className="fab fa-whatsapp"></i>
      </a>

      {/* Price History Modal */}
      {showPriceHistory && selectedProduct && (
        <div className="price-history-modal-overlay" onClick={() => setShowPriceHistory(false)}>
          <div className="price-history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectedProduct.name} ({selectedProduct.englishName})
              </h2>
              <button className="modal-close" onClick={() => setShowPriceHistory(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="current-price-display">
                <span className="label">ಪ್ರಸ್ತುತ ಬೆಲೆ</span>
                <span className="price">₹ {selectedProduct.currentPrice.toLocaleString('en-IN')}</span>
                <span className="unit">ಪ್ರತಿ ಕ್ವಿಂಟಾಲ್</span>
              </div>

              <div className="price-history-list">
                <h3>
                  <i className="fas fa-history"></i> ಹಿಂದಿನ ದರಗಳು
                </h3>
                {selectedProduct.priceHistory && selectedProduct.priceHistory.length > 0 ? (
                  <div className="history-items">
                    {selectedProduct.priceHistory.slice().reverse().map((history, index) => {
                      // Parse the date string properly
                      let dateDisplay = 'Invalid Date';
                      try {
                        const dateStr = history.date;
                        if (dateStr) {
                          // Try to parse the date
                          const date = new Date(dateStr);
                          if (!isNaN(date.getTime())) {
                            dateDisplay = date.toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          } else {
                            // If date parsing fails, just show the string
                            dateDisplay = dateStr;
                          }
                        }
                      } catch (error) {
                        dateDisplay = history.date || 'Unknown date';
                      }

                      return (
                        <div key={index} className="history-item">
                          <div className="history-date">
                            <i className="fas fa-calendar"></i>
                            {dateDisplay}
                          </div>
                          <div className="history-price">
                            ₹ {history.price.toLocaleString('en-IN')}
                          </div>
                          {history.note && (
                            <div className="history-note">
                              <i className="fas fa-info-circle"></i>
                              {history.note}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="no-history">ಹಿಂದಿನ ದರಗಳು ಲಭ್ಯವಿಲ್ಲ</p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <a href="tel:9902671238" className="modal-call-btn">
                <i className="fas fa-phone"></i> ಈಗ ಕರೆ ಮಾಡಿ
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products; 